import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useSkipLinks } from '@evinced-private/ui-common';

import { WfaSessionsSkeleton } from 'src/apps/wfa/components/skeletons';
import { WfaSessionDeletedPopup } from 'src/apps/wfa/components/WfaSessionDeletedPopup';
import { WfaSessionNotSyncedYetPopup } from 'src/apps/wfa/components/WfaSessionNotSyncedYetPopup';
import { useWfaSessionsQuery } from 'src/apps/wfa/hooks/useWfaSessionsQuery';
import { useWfaUsageDataQuery } from 'src/apps/wfa/hooks/useWfaUsageDataQuery';
import { useWfaUserOptionsQuery } from 'src/apps/wfa/hooks/useWfaUserOptionsQuery';
import { WfaSessionsPageError } from 'src/apps/wfa/pages/error-pages/WfaSessionsPageError';
import { IWfaSessionRow } from 'src/apps/wfa/services/WfaDashboardServiceTypes';
import { HorizontalDivider } from 'src/common/components/horizontal-divider/HorizontalDivider';
import { transformCoreLabelsToCustomLabels } from 'src/common/helpers/LabelDisplayHelpers';
import { useEnvironmentConfig } from 'src/common/hooks/useEnvironmentConfig';
import { WFA_SESSIONS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';
import { ILabelConfig } from 'src/common/types/LabelsTypes';

import { WfaSessionsPageHeader } from './sections/header/WfaSessionsHeader';
import { WfaSessionsTable } from './sections/table/WfaSessionsTable';
import { WfaSessionsUsage } from './sections/usage/WfaSessionsUsage';
import { filterLabelOptionsByConfig, getSortedLabelsConfig, wfaSessionsPageStore } from './store';
import {
	WFA_ADDITIONAL_LABBELS_SIZE_LIMIT,
	WFA_SESSIONS_LABEL_KEY_ORDER
} from './WfaSessionsPage.constants';

import './WfaSessionsPage.scss';

export const WfaSessionsPage: FC = () => {
	const [showInitialSkeleton, setShowInitialSkeleton] = useState(true);
	const [selectedRows, setSelectedRows] = useState<IWfaSessionRow[]>([]);
	const { getTableStateById, onFilterChange } = useTableState();
	const { filters, tableSort, paginationInfo } = getTableStateById(WFA_SESSIONS_TABLE_ID);

	const { configuration } = useConfiguration();

	const additionalLabels = (configuration?.PLATFORM?.ADDITIONAL_LABELS ?? []).slice(
		0,
		WFA_ADDITIONAL_LABBELS_SIZE_LIMIT
	);
	const labelsConfig: ILabelConfig[] = transformCoreLabelsToCustomLabels([
		...(configuration?.PLATFORM?.CORE_LABELS ?? []),
		...additionalLabels
	]);

	const isLabelsToggleEnabled = Boolean(
		(configuration?.WFA?.TOGGLES as Record<string, boolean>)?.ENABLE_PLATFORM_LABELS
	);

	const { isNewPlatformEnabled, unitIdDisplayName, unitIdLabelConfig } = usePlatformConfigStore();

	const { environmentDisplayName, environmentLabelConfig } = useEnvironmentConfig();

	const handleClearSelection = useCallback((): void => {
		setSelectedRows([]);
	}, []);

	const handleSelectRow = useCallback((row: IWfaSessionRow, isSelect: boolean): void => {
		if (isSelect) {
			setSelectedRows((prev) => [...prev, row]);
		} else {
			setSelectedRows((prev) => prev.filter((r) => r !== row));
		}
	}, []);

	const handleSelectAll = useCallback((isSelect: boolean, rows: IWfaSessionRow[]): void => {
		setSelectedRows(isSelect ? rows : []);
	}, []);

	const userOptionsQuery = useWfaUserOptionsQuery();

	const { labelOptions, getLabelOptions, isLabelOptionsLoading, resetLabelOptions } =
		wfaSessionsPageStore();

	useEffect(() => {
		getLabelOptions();
	}, [getLabelOptions]);
	useEffect(() => {
		return () => {
			resetLabelOptions();
		};
	}, [resetLabelOptions]);

	const sortedLabelsConfig = useMemo(
		() => getSortedLabelsConfig(labelsConfig, WFA_SESSIONS_LABEL_KEY_ORDER),
		[labelsConfig]
	);

	const filteredLabelOptions = useMemo(
		() => filterLabelOptionsByConfig(labelOptions, sortedLabelsConfig),
		[labelOptions, sortedLabelsConfig]
	);

	const usageDataQuery = useWfaUsageDataQuery(filters);

	const sessionsQuery = useWfaSessionsQuery({
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		sort: tableSort?.[0],
		filters
	});
	useSkipLinks(!usageDataQuery.isLoading && !sessionsQuery.isLoading);

	const isFilterOptionsLoading = useMemo(
		() => userOptionsQuery.isLoading || (isLabelsToggleEnabled && isLabelOptionsLoading),
		[userOptionsQuery.isLoading, isLabelsToggleEnabled, isLabelOptionsLoading]
	);
	const isPrimaryLoading =
		isFilterOptionsLoading || usageDataQuery.isLoading || sessionsQuery.isLoading;

	useEffect(() => {
		if (!isPrimaryLoading && showInitialSkeleton) {
			setShowInitialSkeleton(false);
		}
	}, [isPrimaryLoading, showInitialSkeleton]);

	const unfilteredTotal = sessionsQuery.data?.unfilteredTotal ?? 0;

	if (sessionsQuery?.isError || usageDataQuery?.isError) {
		return <WfaSessionsPageError />;
	}

	if (!sessionsQuery?.isLoading && !sessionsQuery?.isPreviousData && unfilteredTotal === 0) {
		return (
			<>
				<WfaSessionsPageError isNoData />;
				<WfaSessionNotSyncedYetPopup />
				<WfaSessionDeletedPopup />
			</>
		);
	}

	if (showInitialSkeleton) {
		return <WfaSessionsSkeleton />;
	}

	return (
		<div className="wfa-sessions-page">
			<WfaSessionsPageHeader
				filters={filters}
				onFilterChange={onFilterChange}
				labelOptions={filteredLabelOptions}
				userOptions={userOptionsQuery.data}
				isFilterOptionsLoading={isFilterOptionsLoading}
			/>
			<HorizontalDivider />
			<WfaSessionsUsage
				data={usageDataQuery.data}
				isLoading={usageDataQuery.isLoading}
				selectedPeriodLabel={filters?.sessionsPeriod?.[0]?.label}
			/>
			<WfaSessionsTable
				sessionsQuery={sessionsQuery}
				unfilteredTotal={unfilteredTotal}
				isLabelsToggleEnabled={isLabelsToggleEnabled}
				isNewPlatformEnabled={isNewPlatformEnabled}
				unitIdDisplayName={unitIdDisplayName}
				unitIdLabelConfig={unitIdLabelConfig}
				environmentDisplayName={environmentDisplayName}
				environmentLabelConfig={environmentLabelConfig}
				selectedRows={selectedRows}
				onSelectRow={handleSelectRow}
				onSelectAll={handleSelectAll}
				onClearSelection={handleClearSelection}
			/>
			<WfaSessionNotSyncedYetPopup />
			<WfaSessionDeletedPopup />
		</div>
	);
};
